import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CInput } from "@coreui/react";
import { fetchCurrentUser, setSelected } from "./redux/rolesSagas";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { Add } from "./smartComponents/Add";
import { Edit } from "./smartComponents/Edit";
import { Delete } from "./smartComponents/Delete";
import { Assign } from "./smartComponents/Assign";
import { Copy } from "./smartComponents/Copy";
import { api } from "./services/api";
import { xlsxExport } from "../../../utils/xlsxExport";
import { useSearch } from "./hooks/useSearch";
import { usePopup } from "./hooks/usePopup";
import "./roles.scss";
import {
  Clients,
  Download,
  Trash,
  UserPen,
  Copy as CopyIcon,
  Edit as EditIcon
} from "../../../assets/images/svg";

export function RolesModule({ params }) {
  const dispatch = useDispatch();
  const { isLoading, roles } = useSelector((state) => state.roles);

  const { search, setSearch, filteredRoles } = useSearch(roles);

  const { popup, openPopup, closePopup } = usePopup();

  const handlePopup = (info, type) => {
    dispatch(setSelected(info));

    openPopup(type);
  };

  const exportXlsx = async (id, orgName, roleName, roleAssigningUsers) => {
    try {
      const { data } = roleAssigningUsers
        ? await api.getUsersWhoCanAssignRole(id)
        : await api.getUsersPerRole(id);

      const filteredData = roleAssigningUsers ? data : data.filter((user) => user.assigned);
      const formattedData = filteredData.map(
        ({ first_name, last_name, email, role_name, organization }) => [
          roleAssigningUsers ? organization : orgName,
          roleAssigningUsers ? role_name : roleName,
          first_name,
          last_name,
          email
        ]
      );

      xlsxExport({
        data: formattedData,
        headers: ["Organization", "Role Name", "First Name", "Last Name", "Email"],
        fileName: roleAssigningUsers ? "who-can-assign-role" : "user-list",
        download: true
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    dispatch(fetchCurrentUser(params));
  }, []);

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation />
      </div>
    );

  return (
    <div className="roles">
      <div className="roles__search">
        <p>Name</p>
        <div className="roles__search__input">
          <CInput
            onChange={(e) => setSearch(e.target.value)}
            className="text-input"
            type="text"
            placeholder="Search Role"
            value={search}
          />
          <div onClick={() => setSearch("")} className="roles__search__close">
            x
          </div>
        </div>
      </div>
      <div className="roles__add">
        <button onClick={() => openPopup("add")} className="button button--primary">
          Add New Role
        </button>
      </div>

      <div className="roles__content">
        <ul className="group">
          {Object.keys(filteredRoles).map((role) => {
            return (
              <Fragment key={role}>
                <div style={{ fontWeight: "bold", marginBottom: 10, fontSize: "22px" }}>{role}</div>
                {filteredRoles[role].map(
                  ({
                    name,
                    tag,
                    permissions_tag,
                    id,
                    organization_id,
                    users_count,
                    count_users_who_can_assign,
                    permissions,
                    reporting_portal_permissions
                  }) => {
                    let isLastRole = roles[role].length === 1;
                    let roleInfo = {
                      roleId: id,
                      name,
                      permissions,
                      reporting_portal_permissions,
                      organization: { name: role, id: organization_id }
                    };
                    return (
                      <li color="dark" className="group__item" key={id + name}>
                        <div className="group__item__el text">
                          {name + " " + tag + " "}
                          <span className="no-permissions">{permissions_tag}</span>
                        </div>
                        <div className="group__item__el ">
                          <span
                            className="group__icon"
                            onClick={() => handlePopup(roleInfo, "assign")}
                            title="Assign Users"
                          >
                            <Clients />
                          </span>
                        </div>
                        <div className="group__item__el ">
                          <span
                            className="group__icon"
                            onClick={() => handlePopup(roleInfo, "edit")}
                            title="Edit Role"
                          >
                            <EditIcon />
                          </span>
                        </div>
                        {count_users_who_can_assign > 0 ? (
                          <div className="group__item__el ">
                            <span
                              className="group__icon"
                              onClick={() => exportXlsx(id, role, name, true)}
                              title="Who can assign role"
                            >
                              <UserPen />
                            </span>
                          </div>
                        ) : null}

                        {users_count > 0 ? (
                          <div className="group__item__el ">
                            <span
                              className="group__icon"
                              onClick={() => exportXlsx(id, role, name)}
                              title="Export List"
                            >
                              <Download />
                            </span>
                          </div>
                        ) : null}

                        {users_count > 0 ? (
                          <div className="group__item__el ">
                            <span
                              className="group__icon"
                              onClick={() => handlePopup(roleInfo, "copy")}
                              title="Copy Role"
                            >
                              <CopyIcon />
                            </span>
                          </div>
                        ) : null}

                        {!isLastRole && users_count === 0 ? (
                          <div className="group__item__el delete">
                            <span
                              className="group__icon"
                              onClick={() => handlePopup(roleInfo, "delete")}
                              title="Delete"
                            >
                              <Trash color="#FF0000" />
                            </span>
                          </div>
                        ) : null}
                      </li>
                    );
                  }
                )}
              </Fragment>
            );
          })}
        </ul>
      </div>

      {popup.add && <Add isOpen={popup.add} onClose={closePopup("add")} />}
      {popup.edit && <Edit isOpen={popup.edit} onClose={closePopup("edit")} />}
      {popup.assign && <Assign isOpen={popup.assign} onClose={closePopup("assign")} />}
      {popup.copy && <Copy isOpen={popup.copy} onClose={closePopup("copy")} />}
      {popup.delete && <Delete isOpen={popup.delete} onClose={closePopup("delete")} />}
    </div>
  );
}
